import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@frolight/components/Button';
import * as usersService from '@frolight/services/api/usersService';
import CountryInformation from '@frolight/utils/CountryInformation';
import useLocalizedFormattedText from '@frolight/hooks/useLocalizedFormattedText';
import i18next from '@frolight/i18n';

const UserRegistrationForm = ({ onSignUpSuccess }) => {
  const { lftext } = useLocalizedFormattedText();

  const [ firstName, setFirstName ] = useState(null);
  const [ lastName, setLastName ] = useState(null);
  const [ email, setEmail ] = useState(null);
  const [ password, setPassword ] = useState(null);
  const [ passwordConfirmation, setPasswordConfirmation ] = useState(null);
  const [ mobilePhoneNrCountry, setMobilePhoneNrCountry ] = useState('-1');
  const [ mobilePhoneNrNationalNumber, setMobilePhoneNrNationalNumber ] = useState(null);
  const [ loading, setLoading ] = useState(false);
  const [ registrationErrors, setRegistrationErrors ] = useState([]);

  const topCountries = useMemo(
    () => CountryInformation.getTopCountries(),
    []
  );
  const nonTopCountries = useMemo(
    () => CountryInformation.getNonTopCountries(),
    []
  );

  const validateForm = () => {
    return (
      firstName && firstName.length
      && lastName && lastName.length
      && email && email.length
      && (
        (mobilePhoneNrCountry === '-1' && !mobilePhoneNrNationalNumber)
        || (
          mobilePhoneNrCountry !== '-1' && mobilePhoneNrCountry.length
          && mobilePhoneNrNationalNumber && mobilePhoneNrNationalNumber.length
        )
      )
      && password && password.length
      && passwordConfirmation && passwordConfirmation.length
      && password === passwordConfirmation
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRegistrationErrors([]);
  
    try {
      const createUserResponse = await usersService.createUser(
        new AbortController(),
        {
          firstName,
          lastName,
          email,
          mobilePhoneNrCountry,
          mobilePhoneNrNationalNumber,
          password,
          currentLocale: i18next.language
        }
      );
  
      if (createUserResponse) {
        onSignUpSuccess(createUserResponse.data);
      }
    } catch (err) {
      if (err?.errorData?.path && err.errorData.path.length) {
        const errors = [];
        if (err.errorData.path.some(p => ['mobilePhoneNrCountry', 'mobilePhoneNrNationalNumber'].includes(p))) {
          errors.push('mobile_phone_nr');
        }
        if (err.errorData.path.some(p => ['email'].includes(p))) {
          errors.push('email');
        }
        if (err.errorData.path.some(p => ['currentLocale'].includes(p))) {
          errors.push('locale');
        }
        setRegistrationErrors(errors);
      }
    } finally {
      setLoading(false);
    }
  };
  

  const createPhoneCountryCodeOptions = () => {
    const countryInfoToSelectOption = (country, i) => {
      return (
        <option
          key={ `countryCode-${i}-${country.code}` }
          value={ country.code }
        >
          {lftext(`country_names.${country.code}`)} { CountryInformation.getFlagEmoji(country.code) }
        </option>
      );
    }

    const topCountriesOpts = topCountries.map(country => {
      return countryInfoToSelectOption(country);
    });
    const otherCountriesOpts = nonTopCountries.map(country => {
      return countryInfoToSelectOption(country);
    });

    return (
      <>
        <option
          hidden
          disabled
          value="-1"
        >
          {lftext('pages.device_registration.form.mobile_phone.country')}
        </option>
        <optgroup label={lftext('pages.device_registration.form.mobile_phone.common_used')}>
          {topCountriesOpts}
        </optgroup>
        <optgroup label={lftext('pages.device_registration.form.mobile_phone.other_countries')}>
          {otherCountriesOpts}
        </optgroup>
      </>
    );
  };

  const renderError = (errors) => {
    if (errors.length) {
      switch (errors[0]) {
        case 'locale':
          return (
            <p className="feedback_message  feedback_message--error">
              {lftext('pages.device_registration.form.error_messages.something_went_wrong')}
            </p>
          );
        default:
          return (
            <p className="feedback_message  feedback_message--error">
              {lftext('pages.device_registration.form.error_messages.contains_errors')}
            </p>
          );
      }
    }
  }


  return (
    <>
      <form
        className="form  form--user_registration"
        onSubmit={onSubmit}
        method="post"
      >
        <div className="form__title">
          <h2>{lftext('pages.device_registration.form.title')}</h2>
        </div>
        {
          registrationErrors && renderError(registrationErrors)
        }
        <div className="form__field">
          <div className="text_input">
            <label
              className="text_input__label"
              htmlFor="user-registration-first-name"
            >
              {lftext('pages.device_registration.form.first_name')}
            </label>
            <input
              id="user-registration-first-name"
              className="text_input__field"
              type="text"
              name="first-name"
              required
              autoFocus
              disabled={loading}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
        </div>
        <div className="form__field">
          <div className="text_input">
            <label
              className="text_input__label"
              htmlFor="user-registration-last-name"
            >
              {lftext('pages.device_registration.form.last_name')}
            </label>
            <input
              id="user-registration-last-name"
              className="text_input__field"
              type="text"
              name="last-name"
              required
              disabled={loading}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        <div className="form__field">
          <div className={ `text_input text_input--email${registrationErrors.includes('email') ? '  text_input--error' : ''}` }>
            <label
              className="text_input__label"
              htmlFor="user-registration-email"
            >
              {lftext('pages.device_registration.form.email')}
            </label>
            <input
              id="user-registration-email"
              className="text_input__field"
              type="email"
              name="email"
              required
              disabled={loading}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {
            registrationErrors.includes('email') &&
            <div className="form__grid_item  form__grid_item--full">
              <p className="form__inline_error">
                {lftext('pages.device_registration.form.error_messages.email_already_exists')}
              </p>
            </div>
          }
        </div>
        <div className="form__grid">
          <div className="form__grid_item form__grid_item--small">
            <div className={ `form__field` }>
              <div className={ `select_input${registrationErrors.includes('mobile_phone_nr') ? '  select_input--error' : ''}` }>
                <label
                  className="select_input__label"
                  htmlFor="user-registration-mobile-phone-country"
                >
                  {
                    lftext('pages.device_registration.form.mobile_phone.label')
                  }
                </label>
                <select
                  className="select_input__field"
                  id="user-registration-mobile-phone-country"
                  name="mobile-phone-country"
                  defaultValue={mobilePhoneNrCountry}
                  disabled={loading}
                  onChange={(e) => setMobilePhoneNrCountry(e.target.value)}
                >
                  { createPhoneCountryCodeOptions() }
                </select>
              </div>
            </div>
          </div>
          <div className="form__grid_item form__grid_item--large">
            <div className="form__field">
            <div className={ `select_input${registrationErrors.includes('mobile_phone_nr') ? '  text_input--error' : ''}` }>
                <input
                  className="text_input__field"
                  id="user-registration-mobile-phone-national-number"
                  type="tel"
                  name="mobile-phone-national-number"
                  disabled={loading}
                  onChange={(e) => setMobilePhoneNrNationalNumber(e.target.value)}
                  placeholder="e.g. 123 45 67 89"
                />
              </div>
            </div>
          </div>
          {
            registrationErrors.includes('mobile_phone_nr') &&
            <div className="form__grid_item  form__grid_item--full">
              <p className="form__inline_error">
                {lftext('pages.device_registration.form.mobile_phone.invalid')}
              </p>
            </div>
          }
          <div className="form__grid_item  form__grid_item--full">
            <p className="form__notice">
              {lftext('pages.device_registration.form.mobile_phone.description')}
            </p>
          </div>
        </div>
        
        <div className="form__field">
          <div className="text_input  text_input--password">
            <label
              className="text_input__label"
              htmlFor="user-registration-password"
            >
              {lftext('pages.device_registration.form.password')}
            </label>
            <input
              id="user-registration-password"
              className="text_input__field"
              type="password"
              name="password"
              required
              disabled={loading}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        <div className="form__field">
          <div className="text_input  text_input--password">
            <label
              className="text_input__label"
              htmlFor="user-registration-password_confirmation"
            >
              {lftext('pages.device_registration.form.confirm_password')}
            </label>
            <input
              id="user-registration-password_confirmation"
              className="text_input__field"
              type="password"
              name="password-confirmation"
              required
              disabled={loading}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
            />
          </div>
        </div>
        <div className="form__actions">
          <Button
            type="submit"
            className="button"
            label={lftext('pages.device_registration.form.submit')}
            priority="primary"
            disabled={loading || !validateForm()}
          />
        </div>
      </form>
    </>
  );
}

UserRegistrationForm.propTypes = {
  onSignUpSuccess: PropTypes.func.isRequired
}

export default UserRegistrationForm;